import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import produce from 'immer';
import { SentryService } from '../../services';
import { InitUser, ResetState } from '../user/user.actions';
import { InitApp, SetDisabledCaptcha, SetMaintenanceStatus, SetRefreshRequiredStatus } from './app.actions';
import { SignInAgent } from '../agent/agent.actions';
import { Subscription } from 'rxjs';

export interface AppStateModel {
  isInMaintenance: boolean;
  isRefreshByModalRequired: boolean;
  lastRefreshed: number | null;
  disableCaptcha: boolean;
}

export const appStateDefaults = {
  isInMaintenance: false,
  isRefreshByModalRequired: false,
  lastRefreshed: null,
  disableCaptcha: false,
};

@State<AppStateModel>({
  name: 'app',
  defaults: appStateDefaults,
})
@Injectable({ providedIn: 'root' })
export class AppState {
  oktaAuthStateSubscription: Subscription | null = null;

  constructor(private store: Store, private sentry: SentryService) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static isRefreshByModalRequired(state: AppStateModel): boolean {
    return state.isRefreshByModalRequired;
  }

  @Selector()
  static disableCaptcha(state: AppStateModel): boolean {
    return state.disableCaptcha;
  }

  @Selector()
  static isInMaintenance(state: AppStateModel): boolean {
    return state.isInMaintenance;
  }

  @Selector()
  static lastRefreshed(state: AppStateModel): number | null {
    return state.lastRefreshed;
  }

  @Action(InitApp)
  initApp(ctx: StateContext<AppStateModel>, action: InitApp) {
    if (action.useRecoveryToken) {
      ctx.dispatch(new ResetState());
    } else if (action.agentOtp) {
      ctx.dispatch(new SignInAgent(action.agentOtp));
      return;
    } else {
      ctx.dispatch(new InitUser());
    }
  }

  @Action(SetDisabledCaptcha)
  setDisabledCaptcha(ctx: StateContext<AppStateModel>, action: SetDisabledCaptcha) {
    ctx.setState(
      produce((draft) => {
        draft.disableCaptcha = action.disableCaptcha;
      })
    );
  }

  @Action(SetMaintenanceStatus)
  setMaintenanceStatus(ctx: StateContext<AppStateModel>, action: SetMaintenanceStatus) {
    ctx.setState(
      produce((draft) => {
        draft.isInMaintenance = action.isInMaintenance;
      })
    );
  }

  @Action(SetRefreshRequiredStatus)
  setRefreshRequiredStatus(ctx: StateContext<AppStateModel>, action: SetRefreshRequiredStatus) {
    ctx.setState(
      produce((draft) => {
        draft.isRefreshByModalRequired = action.isRefreshByModalRequired;

        if (!action.isRefreshByModalRequired) {
          draft.lastRefreshed = moment().unix();
        }
      })
    );
  }
}
